import {
  BACK_WITH_YOU_STAGES,
  QUESTION_THREAD,
  backWithYouItems,
  mentionItem,
  topWork,
} from '~/utils/policy-studio/back-with-you'

/** The "Back with you" panel's shared state: the toggle in a section header
 *  and the panel in the studio layout are two views of one open flag. The pile
 *  is the handoffs to your teams and you, and every open thread or question
 *  that tags you or one of them, on any policy. Both read the registry. */
export const useBackWithYou = () => {
  const route = useRoute()
  const open = useState('policy-studio-back-with-you', () => true)
  const { teams, tags } = useStudioTeams()
  const threadSeen = usePolicySeen('thread')
  const questionSeen = usePolicySeen('question')
  const { data } = usePolicyList({ stage: [...BACK_WITH_YOU_STAGES] })
  /* The email is '' when the session has none. */
  const mine = computed(() => tags.value.filter(Boolean))
  const { user } = useUserSession()
  const { data: mentions } = usePolicyMentions(mine)
  const unread = (id: string, at: number) =>
    id.startsWith(QUESTION_THREAD)
      ? questionSeen.isUnread(id.slice(QUESTION_THREAD.length), at)
      : threadSeen.isUnread(id, at)
  /* A policy-level handoff card already stands for that policy's questions.
     A thread whose latest comment is yours has nothing new for you — this
     also covers a reply sent from the panel, whose server time is unknown. */
  const items = computed(() => {
    const handoffs = backWithYouItems(
      route.path,
      data.value?.rows ?? [],
      teams.value,
      user.value?.email ?? '',
    )
    const handedOff = new Set(
      handoffs.filter((item) => item.kind === 'question').map((item) => item.id),
    )
    return [
      ...handoffs,
      ...(mentions.value?.rows ?? []).flatMap((row) =>
        row.id.startsWith(QUESTION_THREAD) && handedOff.has(row.policyId)
          ? []
          : [
              mentionItem(
                row,
                mine.value,
                row.author !== user.value?.email && unread(row.id, row.lastAt),
              ),
            ],
      ),
    ]
  })

  const work = computed(() =>
    topWork(data.value?.rows ?? [], user.value?.email ?? ''),
  )

  return { open, items, work }
}
